// DATABASE PROXY CLIENT - This module handles communication between the frontend and database
// Instead of connecting directly to the database, this sends requests to our Express server
// This approach provides better security and allows for query validation

import { getToken } from './localAuth';

// URL endpoint where our Express server handles database requests
const API_URL = '/api/db-proxy';

// RETRY CONFIGURATION - Settings for handling network failures
// When database requests fail, we retry them automatically with increasing delays
const MAX_RETRIES = 2; // Try the request up to 2 additional times if it fails
const INITIAL_RETRY_DELAY = 1000; // Wait 1 second before first retry (1000 milliseconds)

// REQUEST DEDUPLICATION - Prevents multiple identical database queries from running simultaneously
// If the same query is requested while another is already running, return the existing promise
const pendingRequests = new Map<string, Promise<any>>();

// GENERATE CACHE KEY - Creates a unique identifier for database requests
// This allows us to detect when the same request is made multiple times
function getRequestKey(query: string, values: any[]): string {
  return JSON.stringify({ query, values });  // Combine query and parameters into a unique string
}

// EXECUTE DATABASE QUERY - Main function that sends database requests to the server
// Includes automatic retry logic for network failures and error handling
async function executeQuery(query: string, values: any[], _authContext: any): Promise<any> {
  // Track any errors that occur so we can report the final error if all retries fail
  let lastError: Error | null = null;
  
  // Try the request multiple times if it fails (with exponential backoff)
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // Send HTTP request to our Express server with the database query
      const token = getToken();
      const response = await fetch(API_URL, {
        method: 'POST',                          // Use POST to send data in the request body
        headers: {
          'Content-Type': 'application/json',    // Tell server we're sending JSON data
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // cookies not needed for local JWT auth
        body: JSON.stringify({                   // Convert request data to JSON string
          query,           // The SQL query to execute
          params: values,  // Parameters for the SQL query (prevents SQL injection)
        })
      });

      // Check if the server returned an error response
      if (!response.ok) {
        let errorMessage = 'Database query failed';
        let errorDetails = null;
        
        try {
          // Try to parse the error response to get more details
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            // Server returned JSON error data
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
            errorDetails = errorData.details;
          } else {
            // Server returned plain text error (like 502 Bad Gateway)
            const errorText = await response.text();
            if (errorText.includes('502 Bad Gateway')) {
              errorMessage = 'Server temporarily unavailable. Please try again.';
            } else if (errorText) {
              errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
          }
        } catch {
          // If we can't parse the error response, use the default message
        }
        
        // Create an error object with the message and any additional details
        const error = new Error(errorMessage);
        if (errorDetails) {
          (error as any).details = errorDetails;
        }
        
        // Decide whether to retry based on the type of error
        if (response.status >= 500 || response.status === 0) {
          // Server errors (500-599) or network errors (0) - these might be temporary, so retry
          throw error;
        }
        
        // Client errors (400-499) like authentication failures - don't retry these
        throw error;
      }

      // Parse the successful response from the server
      const data = await response.json();
      
      // Special logging for user authentication queries (helps with debugging login issues)
      if (query.includes('users') && query.includes('stack_auth_id')) {
        console.log('db-proxy: User query response', { query, values, data });
      }
      
      // Check if the server returned an error even with a 200 status
      if (data.error) {
        const error = new Error(data.error);
        if (data.details) {
          (error as any).details = data.details;
        }
        throw error;
      }

      // The server returns a PostgreSQL result object with rows, fields, etc.
      // But our database functions expect just the array of rows
      return data.rows || data;
    } catch (error: any) {
      // Store the error in case all retries fail
      lastError = error;
      
      // Don't retry certain types of errors that won't be fixed by retrying
      if (error.message?.includes('Authentication') ||    // User not logged in
          error.message?.includes('Unauthorized') ||     // User doesn't have permission
          error.message?.includes('Forbidden') ||        // Action not allowed
          error.message?.includes('validation')) {       // Query validation failed
        throw error;  // Immediately fail - retrying won't help
      }
      
      // Log network errors for debugging (but only on the first attempt to avoid spam)
      if (attempt === 0 && error.name === 'TypeError' && error.message === 'Failed to fetch') {
        console.error('Database query error:', error);
      }
      
      // If this isn't our last retry attempt, wait and try again
      if (attempt < MAX_RETRIES - 1) {
        // EXPONENTIAL BACKOFF - Wait longer between each retry
        const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);  // 1s, 2s, 4s, etc.
        await new Promise(resolve => setTimeout(resolve, delay));   // Wait before retrying
        continue;  // Try the request again
      }
      
      // All retries failed, throw the error
      throw error;
    }
  }
  
  // This should never be reached (all paths above should return or throw)
  // But included as a safety net in case of unexpected code paths
  throw lastError || new Error('Database query failed after retries');
} // End of executeQuery function

// SQL TEMPLATE FUNCTION - This is the main function that other parts of the app use to run database queries
// It uses JavaScript template literal syntax: sql`SELECT * FROM users WHERE id = ${userId}`
// This automatically handles parameter substitution and prevents SQL injection attacks
export const sql = async (strings: TemplateStringsArray, ...values: any[]) => {
  // Convert the template literal into a PostgreSQL query with numbered parameters
  // Example: sql`SELECT * FROM users WHERE id = ${123}` becomes "SELECT * FROM users WHERE id = $1" with values [123]
  const query = strings.reduce((prev, curr, i) => {
    return prev + curr + (i < values.length ? `$${i + 1}` : '');  // $1, $2, $3, etc.
  }, '');
  
  // Check for pending identical request
  const requestKey = getRequestKey(query, values);
  const pendingRequest = pendingRequests.get(requestKey);
  
  if (pendingRequest) {
    // Return existing promise for identical request
    return pendingRequest;
  }
  
  // Create new request promise
  const requestPromise = (async () => {
    try {
      const result = await executeQuery(query, values, null);
      
      // Remove from pending requests
      pendingRequests.delete(requestKey);
      
      return result;
    } catch (error: any) {
      // Remove from pending requests
      pendingRequests.delete(requestKey);
      
      // Add query info to error for debugging
      error.query = query;
      error.params = values;
      throw error;
    }
  })();
  
  // Store in pending requests
  pendingRequests.set(requestKey, requestPromise);
  
  return requestPromise;  // Return the promise to the caller
}; // End of sql template function

// RAW SQL EXECUTOR - Use when building dynamic queries programmatically
// Provide a query string that already contains numbered placeholders ($1, $2, ...)
// along with a matching values array. Safer for dynamic UPDATE builders.
export async function sqlRaw(query: string, values: any[]) {
  return executeQuery(query, values, null);
}

// Helper to get auth context (reusing existing logic)
// Deprecated: getAuthContext no longer required with Bearer auth

// Check database connection (for compatibility)
export async function checkDatabaseConnection(): Promise<boolean> {
  try {
    const authContext = await getCurrentAuthContext();
    const result = await executeQuery('SELECT 1 as test', [], authContext);
    return result && result.length > 0;
  } catch (error) {
    console.error('Database connection check failed:', error);
    return false;
  }
}
